"use strict";
/**
 * Novig adapter.
 *
 * Orders are IOC buys of an outcome (ai/novig/phase-5-orders.md): the build
 * sends intent, the server prices it from its live book and answers with
 * Novig's final result. A sell is a buy of the other outcome, sized by the
 * server from its positions (ai/NOVIG_VENUE_PLAN.md phase 6b).
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.novigAdapter = void 0;
const novig_fetcher_1 = require("../markets/novig-fetcher");
const cache_1 = require("../markets/cache");
const novig_1 = require("../api/novig");
const novig_creation_1 = require("../orders/novig-creation");
const novig_orderbook_1 = require("../novig-orderbook");
const orderbook_1 = require("../orderbook");
const constants_1 = require("../markets/constants");
/** The order went out; neither Novig's stream nor REST said how it ended. */
const NOVIG_UNCONFIRMED = "Novig order sent, but its result is unknown — it may have filled. Check your Novig positions before trying again.";
exports.novigAdapter = {
    id: "novig",
    label: "Novig",
    orderErrorLabel: "Novig order",
    capabilities: {
        leagueSelector: "flat-list",
        // Every outcome carries a `name`, so both slots are labelled.
        sideLabels: "both",
        // The server sells the whole net holding from its positions cache.
        sellFullPosition: true,
        // A parallel or additional leg, from its stored outcome ids (phase 9).
        fanOutTarget: true,
        // The layouts are venue-neutral: per-button cent prices snap DOWN to the grid on the server.
        sportModes: true,
        // `qty` is 1¢-payout units, so 0.01 of a contract is expressible.
        // Novig's own minimum is unknown; ORDER_TOO_SMALL would say.
        fractionalQuantity: true,
        // A fixed non-uniform grid: src/shared/novig-price-grid.ts.
        priceTicks: "venue-table",
        orderbookIntervalMs: 250,
        pnlDetail: true,
        marketOrder: "type-then-title",
    },
    fetchEvents(query) {
        return (0, novig_fetcher_1.fetchNovigEvents)(query.category);
    },
    fetchEventMarkets(eventId) {
        return (0, novig_fetcher_1.fetchNovigEventMarkets)(eventId);
    },
    async fetchLeagues() {
        const leagues = await (0, novig_1.listNovigLeagues)();
        return leagues.map((league) => ({
            value: league,
            label: (0, constants_1.novigLeagueLabel)(league),
            // Sort on the raw code: the label carries a leading emoji, which
            // would sort before every letter and defeat the priority map.
            sortKey: league,
        }));
    },
    cachedEvents() {
        return (0, cache_1.getNovigEventCache)().map(novig_fetcher_1.novigEventToDisplayEvent);
    },
    startOrderbook(market, opts) {
        return (0, novig_orderbook_1.startNovigOrderbookPolling)(market.id, (response) => {
            // null is "not ready yet" (the book has no snapshot, or its
            // outcome ordering is unresolved), not an error — the widget
            // shows no book and the poll keeps going.
            opts.onUpdate(response
                ? (0, orderbook_1.parseDisplayOrderbook)(response.display, response.ageMs ?? null)
                : null);
        }, opts.onError, opts.intervalMs ?? this.capabilities.orderbookIntervalMs);
    },
    buildOrderRequest(ctx) {
        return (0, novig_creation_1.createNovigOrderRequest)(ctx);
    },
    async placeOrder(request, ctx) {
        const result = await (0, novig_1.placeNovigOrder)(request);
        const unconfirmed = result.outcome === "unconfirmed";
        return {
            // A partial fill glows green, as on the other venues: something filled.
            succeeded: result.filledContracts > 0,
            // Unconfirmed: no badge asserting a fill Novig never confirmed.
            filledQty: unconfirmed ? null : result.filledContracts,
            openedQty: unconfirmed ? null : result.openedContracts ?? result.filledContracts,
            statusForState: null,
            order: {
                order_id: result.orderId ?? `novig-unconfirmed-${Date.now()}`,
                market_id: ctx.marketId,
                side: ctx.side,
                quantity: result.filledContracts,
                fill_price: result.avgPrice ?? result.limitPrice,
                status: result.outcome,
                created_at: new Date().toISOString(),
            },
            raw: result,
            ...(unconfirmed ? { warning: NOVIG_UNCONFIRMED } : {}),
        };
    },
};
